// See `fr.js`.
export default {
  // Two words these screens need and `core.common` does not carry.
  common: {
    loadMore: 'Load more',
    done: 'Done',
  },
  money: {
    exclTax: '{amount} excl. VAT',
    rateExclTax: '{amount}{unit} excl. VAT',
    perMonth: '/ month',
    approxMonthly: '≈ {amount} / month',
    underMonthly: '< {amount} / month',
    offered: 'Free',
  },
  balance: {
    label: 'Your balance',
    missing: '{amount} short.',
    exempt: 'Exempt: nothing is charged.',
    contract: 'Part of your contract: nothing is charged here.',
  },
  inlineTopUp: {
    title: 'How much do you want to top up?',
    recommended: 'Recommended',
    receive: 'You receive {amount}',
    monthOne: '≈ 1 month covered',
    months: '≈ {months} months covered',
  },
  // The « insufficient credits » modal (`modals/CreditsExhaustedModal.vue`), where every
  // `402 credits_exhausted` ends. The member's version names who can top up.
  creditsExhausted: {
    title: 'Not enough credits',
    lede: 'Nothing was started or charged. Top up your wallet, then start the action again.',
    ledeRetry: 'Nothing was started or charged. Top up, and the action starts again as soon as the credit arrives.',
    costLabel: 'What the action costs',
    notEnough: 'Your balance does not cover this action.',
    neverFunded: 'You have not topped up yet: the credit you buy pays for subtitles, storage, delivery and your subscriptions.',
    coversNow: 'Your balance now covers this action.',
    fineprint: 'Amounts excl. VAT. VAT is added at the payment step.',
    topUpTitle: 'Top up the wallet',
    topUp: 'Top up {amount}',
    topUpAndRetry: 'Top up {amount} and retry',
    retry: 'Retry',
    understood: 'Got it',
    credited: 'The credit has arrived: you can start the action again.',
    creditPending: 'Payment accepted. The credit can take a minute to arrive — the action starts again as soon as it does.',
    creditLate: 'Your payment went through, but the credit has not arrived yet. Try again in a moment.',
    memberTitle: 'The organization’s balance is not enough',
    memberLede: 'Nothing was started. Only billing managers can top up the wallet: ask one of them.',
    memberLedeCost: 'This action costs {amount}. Nothing was started. Only billing managers can top up the wallet: ask one of them.',
    managersLabel: 'Billing managers',
    managersMore: 'and {count} more',
  },
  topUp: {
    title: 'Top up the wallet',
    open: 'Top up',
    lede: 'Choose an amount. The credits land in the wallet as soon as the payment is collected.',
    bonus: '+ {amount} free',
    noBonus: 'No bonus',
    customLabel: 'Other amount (€ excl. tax)',
    customHint: 'Between {min} and {max}',
    confirm: 'Continue',
    opening: 'Opening the top-up…',
    paid: 'Payment collected: the credits are in your wallet.',
    pending:
      'The top-up is open and the payment is not confirmed yet. It stays listed under your top-ups, where you can check where it got to.',
    seeHistory: 'See my top-ups',
    uncollectable: {
      title: 'The payment cannot be collected here',
      body:
        'The top-up is open and the invoicing service is waiting to be paid, but it did not send what this page needs to confirm the payment. Nothing was charged. The top-up stays listed under your top-ups, where you can follow and check its state.',
    },
    payTitle: 'Pay a top-up',
    incoming:
      'A debit is already being collected: {amount} of credits arrive once your bank confirms it. This top-up will be an additional payment.',
    keepsCard:
      'The card you pay with is kept for the automatic top-up, and replaces any card kept before.',
    legalAccept: 'By paying, you accept the {terms} and the {use}.',
    termsOfSale: 'Terms of Sale',
    termsOfUse: 'Terms of Service',
    payment: {
      loading: 'Loading the payment form…',
      pay: 'Pay {amount}',
      payUnknown: 'Pay',
      waiting: 'Payment accepted — waiting for the credits to reach your wallet…',
      collecting:
        'Your payment is recorded and your bank is processing it. Your credits will be added automatically once it is confirmed — for a SEPA Direct Debit, allow 8 days on average. There is nothing else to do: the top-up stays in your history, marked “Debit in progress”.',
      processing:
        'Your payment is being confirmed. Your credits will be added automatically once it is, and the top-up stays in your history.',
      sepaNotice:
        'With SEPA Direct Debit, your credits are added once your bank has confirmed the debit, which takes 8 days on average. For credits available straight away, pay by card.',
      requiresAction:
        'Your bank asked for a confirmation that was not completed. Nothing was charged: you can try again.',
      declined: 'The payment was declined',
      tryAgain: 'Try again',
      scriptUnavailable:
        'The payment form could not be loaded. Check that nothing blocks js.stripe.com, then try again.',
    },
    failed: {
      title: 'The top-up failed',
      requestId: 'Reference to quote: {id}',
      purchaseSurvives:
        'The top-up had already been opened: it appears under your top-ups and nothing was charged. It closes on its own deadline.',
      startAgain: 'Start again',
    },
    recap: {
      amount: 'Amount',
      bonus: 'Reload bonus',
      credited: 'Credited to the wallet',
      taxNote: 'Amounts exclude tax. VAT is computed by the invoicing service, and the amount including tax is shown at the payment step.',
    },
  },
  // The billing identity's step in the top-up; its fields are `settings.billing.identity.*`.
  identity: {
    intro: 'These details appear on your invoices. You only enter them once.',
    back: 'Back',
  },
  autoTopUp: {
    title: 'Automatic top-up',
    on: 'On',
    off: 'Off',
    lede:
      'Your balance never runs dry: under the threshold you choose, we top up for you, on the card kept.',
    sentenceStart: 'Top up',
    sentenceMiddle: 'excl. VAT whenever the balance falls under',
    cardTitle: 'Payment method',
    cardSavedCard: 'Card on file: the one from your last top-up where you kept it.',
    cardSaved: 'On file: {kind}, the one from your last top-up where you kept it.',
    cardUnknown: 'Cannot be checked right now: the invoicing service is not answering.',
    cardNone: 'No card on file. A top-up paid while keeping the card saves it.',
    fineprint: 'VAT is added when it is charged. You can switch the automatic top-up off at any time.',
    noCard:
      'No card is kept for the automatic top-up, so it cannot charge anything. Keep one by paying a top-up with it — or it switches itself off at the next top-up it cannot pay.',
    kinds: {
      card: 'card',
      direct_debit: 'bank account (direct debit)',
      wallet: 'wallet',
      bank_transfer: 'bank transfer',
      other: 'payment method',
    },
    failures:
      'The last {count} automatic top-up(s) were refused by your bank. At {max} in a row it switches itself off.',
    keepCard: 'Keep a card',
    replaceCard: 'Use another card',
    keepCardTitle: 'Keep a card for the automatic top-up',
    switchedOffAfterFailures:
      'The automatic top-up switched itself off after {max} refused payments in a row. Switching it back on asks you to agree again.',
    thresholdLabel: 'Top up when the balance falls under (€ excl. tax)',
    amountLabel: 'Amount of each top-up (€ excl. tax)',
    problems: {
      threshold: 'The threshold is a balance of zero or more.',
      amount: 'A top-up is between {min} and {max}.',
    },
    consent:
      'I agree that the card I keep for it is charged {amount} excluding tax, VAT added, each time the balance falls under {threshold} without my being asked each time. I can switch it off at any moment.',
    switchOn: 'Switch on',
    save: 'Save',
  },
  purchases: {
    status: {
      open: 'Pending',
      completed: 'Credited',
      expired: 'Expired',
      cancelled: 'Cancelled',
      declined: 'Payment declined',
      confirmation: 'Confirmation required',
      collecting: 'Debit in progress',
    },
    detail: {
      open: 'Details',
      title: 'Top-up of {date}',
      kinds: {
        credit_top_up: 'Top-up',
        credit_auto_top_up: 'Automatic top-up',
      },
      paid: 'Paid (incl. tax)',
      bonus: 'of which top-up bonus',
      failedAt: 'on {date}',
      documents: 'Documents',
      invoice: 'Invoice',
      noInvoice: 'The invoice is issued once the payment is collected.',
      creditNote: 'Credit note',
      creditNotes: 'The credit notes for its refunds are in your invoices.',
      seeDocuments: 'See invoices',
      refunds: 'Refunds',
      refundStatus: {
        requested: 'Requested',
        succeeded: 'Refunded',
        failed: 'Failed',
      },
      reclaimed: '{amount} of credits taken back, bonus included pro rata.',
      refundFailed: 'Failed: {reason}',
      close: 'Close',
    },
    recap: {
      reference: 'Reference',
      status: 'State',
      amountExclTax: 'Amount excl. VAT',
      tax: 'VAT',
      credited: 'Credited',
      charged: 'To pay (incl. tax)',
      neverOpened:
        'This top-up never reached the invoicing service: nothing was charged and it closes on its deadline.',
      lastFailure: 'Last payment failure: {reason}',
      confirmationNote: 'Your bank asks you to confirm this payment: resume it with “Pay” to complete it.',
    },
    failureReasons: {
      insufficient_funds: 'insufficient funds',
      instrument_unusable: 'payment method unusable (expired or blocked)',
      authentication_required: 'your bank asks you to confirm the payment',
      refused: 'payment refused by the bank',
      cancelled_by_customer: 'payment cancelled',
      provider_unavailable: 'payment service temporarily unavailable',
      unknown: 'no reason given',
    },
  },
  // The cost disclosures, the price catalogue and the home card's wallet label.
  title: 'Billing',
  wallet: {
    debt: 'This negative balance comes from storage and delivery, which go on being charged at an empty balance, down to {ceiling} below zero. The next top-up settles it first.',
    debtNoCeiling: 'This negative balance comes from storage and delivery, which go on being charged at an empty balance. The next top-up settles it first.',
    title: 'Wallet',
    incoming: '+ {amount} pending, awaiting the debit’s confirmation',
    details: 'Details',
    // The home card's two figures for the month (`CreditsCard.vue`).
    spentLabel: 'Spent this month',
    projectionLabel: 'Estimated month-end',
    available: 'Available',
    balanceLabel: 'Wallet balance',
    monthlyFree: '{amount}/month free',
    lowHint: 'Your balance is getting low — top up to avoid interruptions.',
    criticalHint: 'Your balance is almost empty — top up now to keep your services running.',
    depletedHint: 'Your balance is empty — your shared links, embeds and website are offline until you top up.',
  },
  recurring: {
    monthlyTotal: 'Monthly cost',
    perMonth: '/month',
    manage: 'Manage',
    kinds: {
      channel: 'Channel',
      custom_domain: 'Domain',
    },
  },
  explainer: {
    title: 'Your credits, simply',
    lede: '1 credit = €1 excl. VAT. No subscription: you pay for what you use.',
    free: {
      label: 'Free',
      value: '{amount} every month',
      text: 'Given on each anniversary of your organization. They do not add up: this month\'s replace last month\'s.',
      next: 'Next: {date}.',
      none: 'None',
      noneText: 'This organization receives no free credits: only a person\'s first organization does.',
    },
    bought: {
      label: 'Bought',
      value: 'Never expire',
      bonus: 'Up to +{percent}% bonus depending on the amount.',
      auto: 'You can also top up automatically under a threshold.',
    },
    buys: {
      title: 'What {amount} buys',
      video: '{n} hours of video stored for a month',
      subtitles: '{n} minutes of subtitles, per language',
      delivery: '{n} GB of delivery past the {free} free every month',
      deliveryNeverPaid: '{n} GB of delivery; {free} free every month from the month after your first top-up',
      channel: '{n} month(s) of a channel or a custom domain',
      deliveryNoAllowance: '{n} GB of delivery',
      channelOnly: '{n} month(s) of a channel',
      domain: '{n} month(s) of a custom domain',
      stored: '{n} GB of videos and files stored for a month',
      offered: '{service}: free',
    },
    empty: {
      goesOnLabel: 'At an empty balance, carries on',
      goesOn: 'Your library, playback by your team. Storage and delivery go on being charged, and the balance goes negative.',
      stopsLabel: 'At an empty balance, stops',
      stops: 'Shares, embeds and channels for your visitors; new uploads, whose first month of storage is charged when they arrive; new subtitles.',
    },
    pricing: 'All prices',
    later: 'Later',
  },
  runway: {
    caption: 'left at this rate',
    monthsValue: '≈ {n} months',
    daysValue: '≈ {n} days',
  },
  month: {
    projection: 'Month-end projection ≈ {amount}',
    projectionShort: 'Month-end ≈ {amount}',
    spentLine: 'This month: {amount} spent',
    projectionHint: 'Spent this month, plus the current usage rate over the days left, plus the monthly fees still due this month.',
    explain: 'How the wallet works',
    title: 'This month',
    details: 'Detailed consumption',
    balance: 'Balance',
    spent: 'Spent since the 1st, excl. VAT',
    runway: 'Runway at this rate',
    delivery: 'Free delivery',
    deliveryValue: '{used} of {included}',
    deliveryNone: 'Nothing delivered this month',
  },
  storageCost: {
    cardLabel: 'Estimated cost',
    cardHint: 'Videos at {videoRate}, other files at {fileRate}. Deducted daily from your credits.',
    cardHintBytes: 'Videos and other files at {fileRate}, a video on its source’s size. Deducted daily from your credits.',
  },
  uploadCost: {
    // The upload dialog's line (`CreateNewAssetModal.vue`), before any file is chosen.
    firstMonth: 'The first month of storage is charged when a file arrives and is not refunded: {fileRate} for a file, on its size; {videoRate} for a video, on its duration, measured once the upload is over.',
    firstMonthBytes: 'The first month of storage is charged when a file arrives and is not refunded: {fileRate} for a file or a video, on its size.',
  },
  // The balance banners of the app shell (`components/BalanceBanners.vue`, `banners.ts`). The
  // member's version names no amount and offers no top-up: the wallet is not theirs to read (U6).
  banners: {
    low: 'About {days} days of credits left, at {rate} a day.',
    critical: {
      title: 'Less than a day of credits left. | One day of credits left. | Only {n} days of credits left.',
      text: 'At zero, your shares, embeds and channels go offline for your visitors.',
    },
    empty: {
      title: 'Your balance is empty.',
      text: 'Your shares, embeds and channels are offline for your visitors, and new files cannot be imported; your media library is still available.',
    },
    // A wallet that has never paid, held to its daily allowance of public delivery while credit
    // remains (anti-abuse decision 10): it reopens at the day's turn, and a first top-up lifts it.
    dailyAllowance: {
      title: 'Public delivery paused until tomorrow.',
      text: 'Your organization has never topped up its credit, and today it reached its limit of {allowance} of delivery a day: your shares, embeds and channels are offline for your visitors until tomorrow. A first top-up lifts this limit.',
      // The same, when the server names no figure.
      textUnquoted: 'Your organization has never topped up its credit, and today it reached its delivery limit for the day: your shares, embeds and channels are offline for your visitors until tomorrow. A first top-up lifts this limit.',
    },
    deletion: {
      text: 'Without a top-up, your content may be deleted from {date}.',
      action: 'Top up to keep it',
    },
    confirmation: {
      text: 'Your bank asks you to confirm the {amount} automatic top-up.',
      action: 'Confirm the payment',
    },
    autoTopUpOff: {
      text: 'Automatic top-up is off: your card was declined {n} times in a row.',
      action: 'Turn automatic top-up back on',
    },
    member: {
      text: 'The organization’s shares, embeds and channels are temporarily offline for your visitors. Your media library is still available.',
      dailyAllowance: 'The organization’s shares, embeds and channels are offline for your visitors until tomorrow: the organization has never topped up its credit and reached its delivery limit for the day. A first top-up lifts it; your media library is still available.',
      contact: 'Contact a billing manager.',
    },
    deliveryNear: {
      text: 'You have used {used} of your free {included} of delivery this month. Beyond it: {rate}.',
    },
    deliveryExceeded: {
      text: 'This month’s free {included} of delivery is used up: {billed} billed so far, that is {amount}.',
    },
    seeUsage: 'See consumption',
    autoTopUp: 'Automatic top-up',
    dismiss: 'Dismiss',
    incoming: 'A payment is being collected by direct debit: {amount} of credits arrive once it is confirmed.',
  },
  activate: {
    // "Subscription" is the word that was picked: it states the recurrence
    // without describing a dated debit, which nothing guarantees today.
    channelCost: 'Publishing a channel is a {amount}/month subscription.',
    channelOffered: 'Your channel is free of charge: no subscription is taken for it.',
    // "This custom domain costs X/month" read as a purchase price — it is what a
    // registrar charges. We bill connecting an address, never the address itself.
    domainLabel: 'Connecting an address you already own',
    domainDetail: 'In credits, on top of the channel',
    creditsNote: 'You can take the channel offline whenever you like: the subscription stops.',
    domainNote: 'Videas does not sell domain names: buy yours from a registrar, then connect it here. Debited from your wallet every month.',
  },
  subtitlesCost: {
    label: 'Generated subtitles',
    // vue-i18n's plural: « none | one | several ».
    languages: 'no language | {n} language | {n} languages',
    minutes: '{count} started min',
    formula: '{languages} × {minutes} × {rate}',
    roundedUp: 'Each language is rounded up to the next cent.',
    free: 'Free',
    note: 'Charged to your wallet when the generation succeeds.',
    correctionOnly: 'A correction generates no language: it is free.',
    correctionFree: 'Correcting afterwards is free.',
    corrections: '{languages}: a correction of the transcript, free.',
    speechUnknown: 'If one of these languages is the one spoken in the video, it will be corrected for free.',
    // With no transcript, the price supposes the language spoken is among those chosen.
    spokenAssumed:
      'The price assumes the video is in this language; otherwise, one more language will be charged. | The price assumes the video is in one of these languages; otherwise, one more language will be charged.',
    // The same, when the language supposed heard is asked in several variants, all free with it.
    spokenAssumedGroup:
      'The price assumes the video is in {language}, of which {languages} are then free corrections; otherwise up to {count} more languages will be charged.',
    // With no known duration a total would be a promise we cannot keep: state the rate.
    rateOnly: 'Duration not known yet · {languages}',
    rateOnlyNote: 'The exact cost is computed at the end, on the video’s real duration.',
    offered: '{languages} · subtitling is free for you',
  },
  pricing: {
    free: '{amount} in free credits every month',
    recurringTitle: 'Recurring costs (monthly)',
    usageTitle: 'Pay-as-you-go',
    bonusTitle: 'Reload bonus',
    bonusHint: 'The more you reload, the more bonus credits you get.',
    commission: 'When you monetize a channel, Videas takes a {percent}% commission on your revenue.',
    skus: {
      channels: 'Channel',
      custom_domain: 'Custom domain',
      video_storage: 'Video storage',
      file_storage: 'File storage',
      bandwidth: 'Bandwidth',
      subtitles: 'AI subtitling',
    },
    units: {
      channels: '/month',
      custom_domain: '/month',
      video_storage: '/hour/month',
      file_storage: '/GB/month',
      bandwidth: '/GB',
      subtitles: '/minute',
    },
  },
  prices: {
    title: 'Your prices',
    subtitle: 'The prices excluding VAT your organization is charged today.',
    negotiated: 'Negotiated price',
    until: 'until {date} inclusive',
    nextNegotiated: 'From {date}: {price}, negotiated price',
    nextPublic: 'From {date}: {price}, public price',
    allowance: '{size} free every month',
    noAllowance: 'Charged from the first GB',
    videoBySources: 'By the GB of their sources',
    videoBySourcesDetail: 'Your videos are charged by the GB of their sources, at the file storage price; their encoded versions stay included.',
    videoByHoursFrom: 'By the hour from {date}',
    videoBySourcesFrom: 'By the GB of their sources from {date}',
  },
}
